import React from 'react';
import { Link } from 'react-router-dom';

export const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans">
      {/* Navigation Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/official.png" alt="Verisure" className="h-9 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-4 text-xs font-bold">
            <Link to="/about" className="text-slate-400 hover:text-white transition">About Us</Link>
            <Link to="/technicians" className="text-slate-400 hover:text-white transition">Find Technicians</Link>
            <Link to="/login" className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-xl">
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold rounded-full mb-3">
            Legal & Operational Framework
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white">Terms and Conditions</h1>
          <p className="text-slate-400 text-sm mt-2">Last Updated: September 2026 • Version 2.4</p>
        </div>

        {/* Vital Clarification Alert */}
        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/15 to-red-500/10 border border-orange-500/40 rounded-3xl p-6 mb-10 shadow-xl">
          <div className="flex items-start gap-4">
            <span className="text-3xl shrink-0">⚖️</span>
            <div>
              <h3 className="text-base font-black text-white uppercase tracking-wide">
                1. Fundamental Notice: Non-Insurer Status
              </h3>
              <p className="text-slate-300 text-xs md:text-sm mt-2 leading-relaxed">
                <strong>VERISURE IS NOT AN INSURANCE COMPANY, FINANCIAL UNDERWRITER, OR DIRECT INDEMNIFIER.</strong>{' '}
                Verisure provides an enterprise Software-as-a-Service (SaaS) platform for tracking equipment maintenance cycles, diagnostic telemetry, and breakdown forensics. We enable equipment owners and licensed third-party insurance companies to compile, authenticate, and verify tamper-proof maintenance evidence for insurance claims and asset valuation.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-10 text-sm leading-relaxed text-slate-300 bg-slate-900/60 border border-slate-800 p-6 md:p-10 rounded-3xl">
          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <span className="text-orange-400">2.</span> Acceptance of Terms
            </h2>
            <p>
              By accessing, browsing, registering an equipment profile, or utilizing the Verisure platform (including the web application, mobile PWA, and QR verification endpoints), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, you must not access or use the platform.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <span className="text-orange-400">3.</span> Universal Asset Registration & QR Identity Cards
            </h2>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>
                <strong>Asset Ownership:</strong> Users registering assets warrant that they are the lawful owner, authorized lessee, or designated facility manager of the registered equipment.
              </li>
              <li>
                <strong>QR Code Physical Integrity:</strong> The generated Verisure QR Identity Card must be affixed securely to the designated equipment. Altering, duplicating, or swapping QR badges between different machines constitutes a material breach and invalidates the audit trail.
              </li>
              <li>
                <strong>Tamper-Proof Ledger:</strong> Once a maintenance log is submitted and cryptographically committed, it becomes immutable and cannot be deleted or retroactively modified by the equipment owner or technician.
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <span className="text-orange-400">4.</span> Certified Technicians & Service Obligations
            </h2>
            <p>
              Technicians logging repairs on Verisure must undergo KYC identity verification. Technicians agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2 text-slate-300">
              <li>Submit accurate, unmanipulated before/after photographs of replaced components.</li>
              <li>Accurately declare part authenticity (OEM Certified, Approved Aftermarket, or Generic).</li>
              <li>Enable GPS geotagging during on-site inspections to verify physical equipment presence.</li>
              <li>Maintain professional standards and valid trade qualifications in their registered specialization.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <span className="text-orange-400">5.</span> Insurance Claims & Evidence Dossiers
            </h2>
            <p>
              Verisure compiles certified evidence dossiers based on immutable maintenance logs. However:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2 text-slate-300">
              <li>
                <strong>Third-Party Insurer Jurisdiction:</strong> Final claim approval, payout determinations, deductibles, and policy coverage terms remain exclusively within the contractual agreement between the equipment owner and their chosen insurance company.
              </li>
              <li>
                <strong>Fraud Prevention:</strong> Verisure reserves the right to cooperate with licensed insurance investigators and law enforcement by providing cryptographically authenticated audit trails in cases of suspected insurance fraud.
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <span className="text-orange-400">6.</span> Dynamic Resale Valuation Estimates
            </h2>
            <p>
              The Verisure Dynamic Resale Valuation Engine computes algorithmic market estimates based on historical repair compliance, condition scores, and category depreciation curves. These valuations serve as independent market guidance and do not constitute a guaranteed cash offer or binding appraisal.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <span className="text-orange-400">7.</span> Limitation of Liability & Governing Law
            </h2>
            <p>
              To the maximum extent permitted by applicable law, Verisure and its affiliates shall not be liable for indirect, incidental, or consequential damages resulting from equipment operational failure, maintenance downtime, or insurer claim denials. These terms are governed by the laws of the Federal Republic of Nigeria.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-10 border-t border-slate-800 text-center text-xs text-slate-500">
        <p>&copy; {new Date().getFullYear()} VERISURE. Equipment Maintenance Tracking & Insurance Evidence Platform.</p>
      </footer>
    </div>
  );
};
